import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useUpsertRelanceTemplate } from '@/hooks/queries/useRelances';
import { t } from '@/locales/i18n';
import type { RelanceCanal, RelanceNiveau, RelanceTemplateRow } from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  templates: RelanceTemplateRow[];
}

const NIVEAUX: RelanceNiveau[] = ['1ere', '2eme', 'mise_en_demeure', 'contentieux', 'vip_soft'];
const CANAUX: RelanceCanal[] = ['email', 'whatsapp', 'sms', 'courrier'];

export function TemplatesEditor({
  open,
  onOpenChange,
  entrepriseId,
  templates,
}: Props): JSX.Element {
  const [niveau, setNiveau] = useState<RelanceNiveau>('1ere');
  const [canal, setCanal] = useState<RelanceCanal>('email');
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const upsert = useUpsertRelanceTemplate();

  const current = useMemo(
    () => templates.find((tp) => tp.niveau === niveau && tp.canal === canal),
    [templates, niveau, canal],
  );

  useEffect(() => {
    if (current) {
      setSujet(current.sujet ?? '');
      setContenu(current.contenu);
    } else {
      setSujet('');
      setContenu('');
    }
  }, [current]);

  const handleSave = async (): Promise<void> => {
    try {
      await upsert.mutateAsync({
        id: current?.id,
        entreprise_id: entrepriseId,
        niveau,
        canal,
        sujet: sujet || null,
        contenu,
      });
      toast.success('Template enregistré');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erreur');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('echeances.relances.editTemplates')}</DialogTitle>
          <DialogDescription>
            Variables disponibles : <code>{'{{tier_nom}}'}</code>, <code>{'{{facture_ref}}'}</code>,{' '}
            <code>{'{{montant_du}}'}</code>, <code>{'{{devise}}'}</code>,{' '}
            <code>{'{{date_echeance}}'}</code>, <code>{'{{entreprise_nom}}'}</code>.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormSelect
              label="Niveau"
              value={niveau}
              onChange={(v) => setNiveau(v as RelanceNiveau)}
              options={NIVEAUX.map((n) => ({
                value: n,
                label: t(`echeances.relances.niveau.${n}`),
              }))}
            />
            <FormSelect
              label="Canal"
              value={canal}
              onChange={(v) => setCanal(v as RelanceCanal)}
              options={CANAUX.map((c) => ({
                value: c,
                label: t(`echeances.relances.canal.${c}`),
              }))}
            />
          </div>
          {(canal === 'email' || canal === 'mise_en_demeure_pdf') && (
            <FormField
              label="Sujet"
              value={sujet}
              onChange={(e) => setSujet(e.target.value)}
              fullWidth
            />
          )}
          <FormTextarea
            label="Contenu"
            value={contenu}
            onChange={(e) => setContenu(e.target.value)}
            rows={12}
            fullWidth
            required
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={handleSave} disabled={upsert.isPending || contenu.length === 0}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
